// src/pages/questionnaire/QuestionnaireEdit.js
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import api from '../../services/api'

function QuestionnaireEdit() {
  const { id } = useParams()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [images, setImages] = useState([])
  const [uploading, setUploading] = useState(false)
  const [fields, setFields] = useState([])
  const [fieldLabel, setFieldLabel] = useState('')
  const [fieldType, setFieldType] = useState('text')
  const [fieldOptions, setFieldOptions] = useState('')
  const [fieldRequired, setFieldRequired] = useState(false)
  const [status, setStatus] = useState('')
  const [translatoryLanguage, setTranslatoryLanguage] = useState('en')
  const [textareaSelection, setTextareaSelection] = useState({ start: 0, end: 0 })

  useEffect(() => {
    ;(async () => {
      try {
        const res = await api.get(`/questionnaires/${id}`)
        const q = res.data.questionnaire
        setName(q.name)
        setDescription(q.description)
        setImages(q.images || [])
        setFields(q.fields || [])
      } catch {
        setStatus('Error fetching questionnaire')
      }
    })()
  }, [id])

  const handleFileChange = async e => {
    const files = Array.from(e.target.files)
    if (!files.length) return
    try {
      setUploading(true)
      const data = new FormData()
      files.forEach(f => data.append('images', f))
      const res = await api.post('/upload/images', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setImages(prev => [...prev, ...res.data.urls])
    } finally {
      setUploading(false)
    }
  }

  const handleRemoveImage = idx => {
    setImages(prev => prev.filter((_, i) => i !== idx))
  }

  const handleAddField = () => {
    if (!fieldLabel) return
    const newField = {
      label: fieldLabel,
      type: fieldType,
      options: fieldType === 'select'
        ? fieldOptions.split(',').map(o => o.trim()).filter(Boolean)
        : [],
      required: fieldRequired
    }
    setFields(prev => [...prev, newField])
    setFieldLabel('')
    setFieldType('text')
    setFieldOptions('')
    setFieldRequired(false)
  }

  const handleRemoveField = idx => {
    setFields(prev => prev.filter((_, i) => i !== idx))
  }

  const handleDescriptionSelect = e => {
    setTextareaSelection({ start: e.target.selectionStart, end: e.target.selectionEnd })
  }

  const applyBasicTag = tag => {
    const { start, end } = textareaSelection
    if (start === end) return
    const wrapped =
      description.slice(0, start) +
      `<${tag}>` +
      description.slice(start, end) +
      `</${tag}>` +
      description.slice(end)
    setDescription(wrapped)
  }

  const handleUpdate = async () => {
    if (!name) {
      setStatus('Name required')
      return
    }
    try {
      const body = { name, description, images, fields, translatoryLanguage }
      await api.put(`/questionnaires/${id}`, body)
      setStatus('Questionnaire updated')
    } catch {
      setStatus('Error updating questionnaire')
    }
  }

  return (
    <div className="questionnaire-container">
      <h2>Edit Questionnaire</h2>
      <p>ID: {id}</p>

      <label>Name</label>
      <input value={name} onChange={e => setName(e.target.value)} type="text" />

      <label>Description</label>
      <div className="description-toolbar">
        <button onClick={() => applyBasicTag('strong')}>B</button>
        <button onClick={() => applyBasicTag('em')}>I</button>
        <button onClick={() => applyBasicTag('u')}>U</button>
      </div>
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        onSelect={handleDescriptionSelect}
      />

      <label>Translatory Language</label>
      <input value={translatoryLanguage} onChange={e => setTranslatoryLanguage(e.target.value)} type="text" />

      <label>Upload Images</label>
      <input type="file" multiple onChange={handleFileChange} disabled={uploading} />
      {uploading && <p>Uploading…</p>}

      {images.length > 0 && (
        <div className="image-grid">
          {images.map((url, idx) => (
            <div className="image-item" key={idx}>
              <img src={url} alt="" />
              <button onClick={() => handleRemoveImage(idx)}>✕</button>
            </div>
          ))}
        </div>
      )}

      <h3>Fields</h3>
      <div className="field-builder">
        <label>Field Label</label>
        <input value={fieldLabel} onChange={e => setFieldLabel(e.target.value)} type="text" />

        <label>Field Type</label>
        <select value={fieldType} onChange={e => setFieldType(e.target.value)}>
          <option value="text">Text</option>
          <option value="textarea">Textarea</option>
          <option value="select">Select</option>
          <option value="checkbox">Checkbox</option>
          <option value="number">Number</option>
          <option value="date">Date</option>
        </select>

        {fieldType === 'select' && (
          <>
            <label>Field Options (comma‑separated)</label>
            <input value={fieldOptions} onChange={e => setFieldOptions(e.target.value)} type="text" />
          </>
        )}

        <label>Required</label>
        <input type="checkbox" checked={fieldRequired} onChange={e => setFieldRequired(e.target.checked)} />

        <button onClick={handleAddField}>Add Field</button>
      </div>

      <ul className="field-list">
        {fields.map((f, idx) => (
          <li key={idx}>
            <span>
              {f.label} ({f.type}) {f.required && 'required'}
              {f.options.length ? ` – [${f.options.join(', ')}]` : ''}
            </span>
            <button onClick={() => handleRemoveField(idx)}>Remove</button>
          </li>
        ))}
      </ul>

      <button onClick={handleUpdate}>Update Questionnaire</button>
      <p>{status}</p>
    </div>
  )
}

export default QuestionnaireEdit
